import React, { useState, useEffect } from 'react';
import { useTheme } from '../hooks/useTheme';

const ConfirmDialog = ({ open, title, message, confirmLabel, cancelLabel, onConfirm, onCancel, danger, requireTypedConfirmation }) => {
  const t = useTheme();
  const [typedValue, setTypedValue] = useState('');

  // Reset typed value when dialog opens/closes
  useEffect(() => {
    if (open) setTypedValue('');
  }, [open]);

  if (!open) return null;

  const needsTyping = !!requireTypedConfirmation;
  const typingMatch = needsTyping && typedValue.trim().toLowerCase() === requireTypedConfirmation.trim().toLowerCase();
  const canConfirm = !needsTyping || typingMatch;

  // The icon-tile backgrounds (#fef2f2 / #eff6ff in light mode) are
  // strongly tinted; in dark mode we use semi-transparent overlays so
  // the icon reads against the dark modal panel instead of looking
  // like a glowing white square.
  const iconBg = danger
    ? (t.dark ? 'rgba(239, 68, 68, 0.15)' : '#fef2f2')
    : (t.dark ? 'rgba(59, 130, 246, 0.15)' : '#eff6ff');

  // Typed-confirmation kbd-style highlight — same tinting strategy.
  const codePillBg = t.dark ? 'rgba(148, 163, 184, 0.18)' : '#f1f5f9';

  return (
    <div
      onClick={onCancel}
      // The overlay-level className is what triggers our app-wide
      // body:has(.modal-overlay) { overflow: hidden } scroll lock,
      // so include it here too even though the inline styles already
      // give it the same visual treatment.
      className="modal-overlay"
      style={{
        position: 'fixed', inset: 0, zIndex: 100000,
        backgroundColor: 'rgba(15, 23, 42, 0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'fadeIn 0.15s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: t.modalBg,
          borderRadius: '16px',
          width: '400px',
          maxWidth: '90vw',
          // In dark mode the 0,0,0 inner stroke is invisible against the
          // panel; swap to a subtle light hairline so the modal still
          // reads as elevated.
          boxShadow: t.dark
            ? '0 25px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.06)'
            : '0 25px 60px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.05)',
          animation: 'slideUp 0.2s ease',
          overflow: 'hidden',
          color: t.text,
        }}
      >
        {/* Icon + Title */}
        <div style={{ padding: '24px 24px 0' }}>
          <div style={{
            width: '44px', height: '44px', borderRadius: '12px',
            backgroundColor: iconBg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: '16px',
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke={danger ? '#ef4444' : '#3b82f6'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <h3 style={{
            margin: '0 0 8px', fontSize: '17.5px', fontWeight: 700, color: t.heading,
          }}>
            {title || 'Are you sure?'}
          </h3>
          <p style={{
            margin: 0, fontSize: '15.5px', color: t.textSecondary, lineHeight: 1.5,
          }}>
            {message || 'This action cannot be undone.'}
          </p>

          {/* Typed confirmation input */}
          {needsTyping && (
            <div style={{ marginTop: '16px' }}>
              <p style={{ margin: '0 0 8px', fontSize: '14.5px', color: t.text, lineHeight: 1.5 }}>
                Type <strong style={{ color: t.heading, fontFamily: 'monospace', backgroundColor: codePillBg, padding: '2px 6px', borderRadius: '4px' }}>{requireTypedConfirmation}</strong> to confirm:
              </p>
              <input
                type="text"
                value={typedValue}
                onChange={(e) => setTypedValue(e.target.value)}
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter' && canConfirm) onConfirm(); }}
                placeholder={requireTypedConfirmation}
                style={{
                  width: '100%', padding: '10px 12px', fontSize: '15.5px',
                  borderRadius: '8px',
                  border: `2px solid ${typingMatch ? '#16a34a' : t.cardBorder}`,
                  outline: 'none', boxSizing: 'border-box',
                  fontFamily: 'monospace',
                  backgroundColor: t.inputBg,
                  color: t.inputColor,
                  transition: 'border-color 0.2s',
                }}
              />
            </div>
          )}
        </div>

        {/* Buttons */}
        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: '8px',
          padding: '20px 24px', marginTop: '8px',
        }}>
          <button
            onClick={onCancel}
            style={{
              padding: '8px 18px', borderRadius: '8px', fontSize: '14.5px', fontWeight: 600,
              border: `1px solid ${t.cardBorder}`,
              backgroundColor: t.bg,
              color: t.text,
              cursor: 'pointer', transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = t.bgAlt; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = t.bg; }}
          >
            {cancelLabel || 'Cancel'}
          </button>
          <button
            onClick={onConfirm}
            disabled={!canConfirm}
            style={{
              padding: '8px 18px', borderRadius: '8px', fontSize: '14.5px', fontWeight: 600,
              border: 'none', transition: 'all 0.15s',
              backgroundColor: danger ? '#dc2626' : '#3b82f6',
              color: '#fff',
              opacity: canConfirm ? 1 : 0.4,
              cursor: canConfirm ? 'pointer' : 'not-allowed',
            }}
            onMouseEnter={(e) => { if (canConfirm) e.currentTarget.style.opacity = '0.9'; }}
            onMouseLeave={(e) => { if (canConfirm) e.currentTarget.style.opacity = '1'; }}
          >
            {confirmLabel || 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
